"use client";

import { useEffect, useRef, useState } from "react";

const batches = [
  { id: "KR-2025-001", line: "Red Line", date: "Nov 2025", status: "Pass", alkaloids: "1.42%", contaminants: "ND", heavy_metals: "Pass" },
  { id: "KR-2025-002", line: "White Line", date: "Nov 2025", status: "Pass", alkaloids: "1.38%", contaminants: "ND", heavy_metals: "Pass" },
  { id: "KR-2025-003", line: "Green Line", date: "Dec 2025", status: "Pass", alkaloids: "1.51%", contaminants: "ND", heavy_metals: "Pass" },
  { id: "KR-2026-001", line: "Green Line", date: "Jan 2026", status: "Pass", alkaloids: "1.49%", contaminants: "ND", heavy_metals: "Pass" },
  { id: "KR-2026-002", line: "Red Line", date: "Feb 2026", status: "Pass", alkaloids: "1.44%", contaminants: "ND", heavy_metals: "Pass" },
];

export default function LabProofSection() {
  const [selectedBatch, setSelectedBatch] = useState(batches[0]);
  const refs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    refs.current.forEach((r) => r && observer.observe(r));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28" style={{ backgroundColor: "#F6F5F2" }}>
      <div className="max-w-container mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={(el) => { refs.current[0] = el; }}
          className="reveal mb-16 text-center max-w-2xl mx-auto"
        >
          <span className="section-label mb-4 block mx-auto justify-center">Lab & Transparency</span>
          <h2
            className="font-serif font-bold mb-4"
            style={{
              fontFamily: "Fraunces, Georgia, serif",
              fontSize: "clamp(2rem, 3.5vw, 3rem)",
              letterSpacing: "-0.025em",
              lineHeight: "1.1",
              color: "#1F1F1F",
            }}
          >
            Every Batch.
            <br />
            <em style={{ color: "#3F5A45" }}>Publicly Tested.</em>
          </h2>
          <p
            className="text-base leading-relaxed"
            style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
          >
            We partner with accredited third-party laboratories. Results are published for every batch — no exceptions.
          </p>
        </div>

        {/* COA Viewer */}
        <div
          ref={(el) => { refs.current[1] = el; }}
          className="reveal"
        >
          <div
            className="grid grid-cols-1 lg:grid-cols-3 gap-0"
            style={{ border: "1px solid #E0DFDB", backgroundColor: "white" }}
          >
            {/* Batch selector sidebar */}
            <div
              className="p-6 border-b lg:border-b-0 lg:border-r"
              style={{ borderColor: "#E0DFDB", backgroundColor: "#F6F5F2" }}
            >
              <p
                className="text-xs font-semibold uppercase tracking-widest mb-4"
                style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
              >
                Select Batch
              </p>
              <div className="space-y-2">
                {batches.map((batch) => (
                  <button
                    key={batch.id}
                    onClick={() => setSelectedBatch(batch)}
                    className="w-full text-left p-3 transition-all duration-200"
                    style={{
                      backgroundColor: selectedBatch.id === batch.id ? "white" : "transparent",
                      border: `1px solid ${selectedBatch.id === batch.id ? "#3F5A45" : "transparent"}`,
                      borderRadius: "2px",
                    }}
                  >
                    <div
                      className="text-xs font-semibold mb-0.5"
                      style={{
                        color: selectedBatch.id === batch.id ? "#3F5A45" : "#1F1F1F",
                        fontFamily: "Inter, system-ui, sans-serif",
                      }}
                    >
                      {batch.id}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {batch.line} · {batch.date}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* COA Display */}
            <div className="lg:col-span-2 p-8">
              <div className="flex items-start justify-between mb-8">
                <div>
                  <div
                    className="text-xs font-semibold uppercase tracking-widest mb-1"
                    style={{ color: "#3F5A45", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    Certificate of Analysis
                  </div>
                  <h3
                    className="font-serif font-bold"
                    style={{
                      fontFamily: "Fraunces, Georgia, serif",
                      fontSize: "1.5rem",
                      letterSpacing: "-0.02em",
                      color: "#1F1F1F",
                    }}
                  >
                    Batch {selectedBatch.id}
                  </h3>
                  <p
                    className="text-sm mt-1"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    {selectedBatch.line} · Tested {selectedBatch.date}
                  </p>
                </div>
                <div
                  className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide"
                  style={{
                    backgroundColor: "rgba(63,90,69,0.08)",
                    color: "#3F5A45",
                    fontFamily: "Inter, system-ui, sans-serif",
                    border: "1px solid rgba(63,90,69,0.2)",
                  }}
                >
                  ✓ {selectedBatch.status}
                </div>
              </div>

              {/* Results grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {[
                  { label: "Total Alkaloid Content", value: selectedBatch.alkaloids, desc: "Mitragynine + 7-OH" },
                  { label: "Microbial Contaminants", value: selectedBatch.contaminants, desc: "Salmonella, E. coli, Yeast/Mold" },
                  { label: "Heavy Metals", value: selectedBatch.heavy_metals, desc: "As, Cd, Pb, Hg" },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="p-4"
                    style={{ backgroundColor: "#F6F5F2", border: "1px solid #E0DFDB" }}
                  >
                    <div
                      className="text-xs font-medium mb-2"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {item.label}
                    </div>
                    <div
                      className="font-serif font-bold text-2xl mb-1"
                      style={{
                        fontFamily: "Fraunces, Georgia, serif",
                        color: item.value === "Pass" || item.value === "ND" ? "#3F5A45" : "#1F1F1F",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {item.value}
                    </div>
                    <div
                      className="text-xs"
                      style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                    >
                      {item.desc}
                    </div>
                  </div>
                ))}
              </div>

              {/* Lab info */}
              <div
                className="flex items-center justify-between p-4"
                style={{ backgroundColor: "#F6F5F2", border: "1px solid #E0DFDB" }}
              >
                <div>
                  <p
                    className="text-xs font-semibold mb-0.5"
                    style={{ color: "#1F1F1F", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    Tested by: Cascadia Labs — Portland, OR
                  </p>
                  <p
                    className="text-xs"
                    style={{ color: "#6B6B6B", fontFamily: "Inter, system-ui, sans-serif" }}
                  >
                    ISO/IEC 17025:2017 Accredited · Independent Third-Party
                  </p>
                </div>
                <button
                  className="btn-ghost text-xs"
                  style={{ color: "#3F5A45", borderBottomColor: "#3F5A45" }}
                >
                  Download PDF
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M12 16L7 11M12 16L17 11M12 16V4M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Link to full lab page */}
        <div
          ref={(el) => { refs.current[2] = el; }}
          className="reveal mt-8 text-center"
        >
          <a href="/lab-testing" className="btn-ghost" style={{ color: "#3F5A45", borderBottomColor: "#3F5A45" }}>
            View All Lab Results
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M13 6L19 12L13 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
}